import { type ReactNode } from "react";

import { Button } from "#components/v2/Button";
import { useUIVersion } from "#hooks";
import {
  CheckCircleFillIcon,
  ClearIcon,
  InfoCheckFillIcon,
  LightbulbFillIcon,
  NegativeSolidIcon,
  WarningCircleFillIcon,
} from "#icons";
import { cn, customTv } from "#utils";

import type { VariantProps } from "tailwind-variants";

export type AlertSeverity = "success" | "info" | "warning" | "error" | "secondary";

/** AlertProps interface for the Alert component */
export interface AlertProps extends VariantProps<typeof alertStyles> {
  /** The content of the alert */
  children: ReactNode;
  /** The severity of the alert - determines color and default icon */
  severity?: AlertSeverity;
  /** Visual variant - "normal" (filled background) or "light" (white bg with colored border) */
  variant?: "normal" | "light";
  /** Custom icon to override the default severity icon. Set to false to remove icon */
  icon?: ReactNode | false;
  /** Action element to display at the end of the alert */
  action?: ReactNode;
  /** Callback fired when close button is clicked */
  onClose?: () => void;
  /** Override the default label for the close button */
  closeText?: string;
  /** Additional CSS classes */
  className?: string;
  /** ARIA role for the alert */
  role?: string;
}

const alertStyles = customTv({
  base: "flex flex-col gap-1 p-2 rounded-md text-text-primary text-label-medium",
  variants: {
    severity: {
      success: "",
      info: "",
      warning: "",
      error: "",
      secondary: "",
    },
    uiVersion: {
      v1: "",
      v2: "",
    },
    variant: {
      normal: "",
      light: "",
    },
  },
  compoundVariants: [
    // V2 normal (filled backgrounds — semantic tokens so they follow the theme)
    {
      uiVersion: "v2",
      severity: "success",
      variant: "normal",
      class: "bg-background-positive",
    },
    {
      uiVersion: "v2",
      severity: "info",
      variant: "normal",
      class: "bg-background-informative",
    },
    {
      uiVersion: "v2",
      severity: "warning",
      variant: "normal",
      class: "bg-background-warning",
    },
    {
      uiVersion: "v2",
      severity: "error",
      variant: "normal",
      class: "bg-background-negative",
    },
    {
      uiVersion: "v2",
      severity: "secondary",
      variant: "normal",
      class: "bg-background-secondary",
    },

    // V2 light (themed surface with a border matching the icon color)
    {
      uiVersion: "v2",
      severity: "success",
      variant: "light",
      class: "bg-background-primary border border-positive-600",
    },
    {
      uiVersion: "v2",
      severity: "info",
      variant: "light",
      class: "bg-background-primary border border-informative-600",
    },
    {
      uiVersion: "v2",
      severity: "warning",
      variant: "light",
      class: "bg-background-primary border border-warning-500",
    },
    {
      uiVersion: "v2",
      severity: "error",
      variant: "light",
      class: "bg-background-primary border border-negative-600",
    },
    {
      uiVersion: "v2",
      severity: "secondary",
      variant: "light",
      class: "bg-background-primary border border-neutral-700",
    },

    // V1 normal (existing MUI-like styles)
    {
      uiVersion: "v1",
      severity: "success",
      variant: "normal",
      class: "bg-[#edf7ed] text-[#1e4620] text-label-large font-normal",
    },
    {
      uiVersion: "v1",
      severity: "info",
      variant: "normal",
      class: "bg-[#e5f6fd] text-[#014361] text-label-large font-normal",
    },
    {
      uiVersion: "v1",
      severity: "warning",
      variant: "normal",
      class: "bg-[#fff4e5] text-[#663c00] text-label-large font-normal",
    },
    {
      uiVersion: "v1",
      severity: "error",
      variant: "normal",
      class: "bg-[#fdeded] text-[#5f2120] text-label-large font-normal",
    },
    {
      uiVersion: "v1",
      severity: "secondary",
      variant: "normal",
      class: "bg-neutral-50 text-neutral-700 text-label-large font-normal",
    },

    // V1 light (white background with border matching icon color)
    {
      uiVersion: "v1",
      severity: "success",
      variant: "light",
      class: "bg-white border border-positive-600 text-[#1e4620] text-label-large font-normal",
    },
    {
      uiVersion: "v1",
      severity: "info",
      variant: "light",
      class: "bg-white border border-informative-600 text-[#014361] text-label-large font-normal",
    },
    {
      uiVersion: "v1",
      severity: "warning",
      variant: "light",
      class: "bg-white border border-warning-500 text-[#663c00] text-label-large font-normal",
    },
    {
      uiVersion: "v1",
      severity: "error",
      variant: "light",
      class: "bg-white border border-negative-600 text-[#5f2120] text-label-large font-normal",
    },
    {
      uiVersion: "v1",
      severity: "secondary",
      variant: "light",
      class: "bg-white border border-neutral-700 text-neutral-700 text-label-large font-normal",
    },
  ],
  defaultVariants: {
    severity: "success",
    uiVersion: "v2",
    variant: "normal",
  },
});

const iconStyles = customTv({
  base: "flex-shrink-0 w-6 h-6",
  variants: {
    severity: {
      success: "text-positive-600",
      info: "text-informative-600",
      warning: "text-warning-500",
      error: "text-negative-600",
      secondary: "text-foreground-secondary",
    },
  },
});

// Default icon mapping for each severity
const defaultIcons: Record<AlertSeverity, ReactNode> = {
  success: <CheckCircleFillIcon className="h-6 w-6" />,
  info: <InfoCheckFillIcon className="h-6 w-6" />,
  warning: <WarningCircleFillIcon className="h-6 w-6" />,
  error: <NegativeSolidIcon className="h-6 w-6" />,
  secondary: <LightbulbFillIcon className="h-6 w-6" />,
};

export function Alert({
  children,
  severity = "success",
  variant = "normal",
  icon,
  action,
  onClose,
  closeText = "Close",
  className,
  role = "alert",
}: AlertProps) {
  const uiVersion = useUIVersion();

  // Determine which icon to show
  const shouldShowIcon = icon !== false;
  const iconToShow = icon || (shouldShowIcon ? defaultIcons[severity] : null);

  return (
    <div className={cn(alertStyles({ severity, uiVersion, variant }), className)} role={role}>
      <div className="flex gap-2">
        {iconToShow ? <div className={iconStyles({ severity })}>{iconToShow}</div> : null}

        <div className="flex flex-grow flex-col items-start justify-center">
          <div>{children}</div>
        </div>

        {onClose ? (
          <div className="flex-shrink-0">
            <Button
              aria-label={closeText}
              className="border-none p-1"
              onPress={onClose}
              variant="tertiary"
            >
              <ClearIcon className="h-4 w-4" />
            </Button>
          </div>
        ) : null}
      </div>
      {action ? <div className="flex justify-end">{action}</div> : null}
    </div>
  );
}
